import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { LinkChips } from "@/components/search/plp-seo/LinkChips";
import {
  PLP_SEO_KEYS,
  plpSeoFallback,
} from "@/components/search/plp-seo/plp-seo.i18n";
import {
  PLP_SEO_SECTION_CLASS,
  PLP_SEO_SECTION_HEADING_SIZE,
} from "@/components/search/plp-seo/plp-seo.tokens";
import { filterRelatedPages } from "@/components/search/plp-seo/related-pages.utils";
import type {
  RelatedPage,
  RelationType,
} from "@/features/search/api/related-pages";
import { toHref } from "@/features/search/utils/url.utils";
import { Heading } from "@/ui/cva/Heading";

type Module = {
  key: "relatedSearches" | "discoverMore";
  /** Group order inside the module. */
  groups: RelationType[];
};

/*
 * WHO-5760 (after Vinterior's category pages): "Related searches" narrows the page by what it is
 * made of or looks like (style x category, material x category); "Discover more" goes sideways, to
 * the sibling category, brand, colour and curated pages. The category group repeats links the
 * sidebar and breadcrumbs also carry. Deliberate: it is what the old PLP did.
 */
const MODULES: Module[] = [
  { key: "relatedSearches", groups: ["STYLE", "MATERIAL"] },
  { key: "discoverMore", groups: ["CATEGORY", "BRAND", "COLOR", "CUSTOM"] },
];

const HEADINGS = {
  relatedSearches: {
    key: PLP_SEO_KEYS.relatedSearchesHeading,
    fallback: plpSeoFallback.relatedSearchesHeading,
  },
  discoverMore: {
    key: PLP_SEO_KEYS.discoverMoreHeading,
    fallback: plpSeoFallback.discoverMoreHeading,
  },
} as const;

/**
 * Links to sibling landing pages as chips, in two modules and grouped inside each by what makes
 * them siblings (WHO-5534, WHO-5760). Crawler food: it is how the landing pages reference each
 * other, so a category page is reachable from the brand pages that share its products and vice
 * versa.
 *
 * Pages with fewer than five live products are dropped first; see `related-pages.utils.ts`. A
 * module with no links left renders nothing.
 */
export const RelatedPages = ({ pages }: { pages: RelatedPage[] }) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const linkable = filterRelatedPages(pages);

  return (
    <>
      {MODULES.map(({ key, groups }) => {
        const inModule = linkable.filter(page => groups.includes(page.type));

        if (inModule.length === 0) {
          return null;
        }

        return (
          <section key={key} className={PLP_SEO_SECTION_CLASS}>
            <Heading as="h2" size={PLP_SEO_SECTION_HEADING_SIZE}>
              {t(HEADINGS[key].key, HEADINGS[key].fallback(locale))}
            </Heading>
            {groups.map(type => (
              <LinkChips
                key={type}
                heading={t(
                  `listing:listing-related-pages-by-${type.toLowerCase()}`
                )}
                links={inModule
                  .filter(page => page.type === type)
                  .map(page => ({ href: toHref(page.url), title: page.title }))}
              />
            ))}
          </section>
        );
      })}
    </>
  );
};
